import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { accountService, transferService, loanService } from '../../../apiServices';
import { useRefresh } from '../../../contexts/RefreshContext';
import { Account, CreateAccountRequest, UpdateAccountRequest, Transfer, TransferType, Loan, LoanType, LoanStatus, CreateLoanRequest, UpdateLoanRequest, Currency } from '../../../apiServices/types';
import { useNotification } from '../../../contexts/NotificationContext';
import { formatCurrency, ValidationConstants, ValidationUtils, getCurrencyName, parseCurrency, toLocalDateString, toUTCDateString } from '../../../utils';
import { EditIcon, DeleteIcon, TransferIcon, StarIcon } from '../../UI/Icons';
import { Modal, ConfirmationModal, GlassSelect } from '../../UI';
import { TransferModal } from '../../TransferModal';
import LoanModal from '../../LoanModal';
import { loanPaymentService } from '../../../apiServices/loanPaymentService';
import { LoanPayment, CreateLoanPaymentRequest } from '../../../apiServices/types';
import TotalCapital from './components/TotalCapital';
import './AccountsPage.css';
import '../BudgetsPage/BudgetsPage.css';

type TabType = 'accounts' | 'transfers' | 'loans';

// Helper function to check if loan type is ILent (handles both string and number from backend)
const isILent = (loanType: LoanType | string | number): boolean => {
  return String(loanType) === 'ILent' || loanType === LoanType.ILent || loanType === 0;
};

// Helper function to parse loan status (handles both string and number from backend)
const parseLoanStatus = (status: LoanStatus | string | number): LoanStatus => {
  if (typeof status === 'string') {
    if (status === 'Active') return LoanStatus.Active;
    if (status === 'PartiallyRepaid') return LoanStatus.PartiallyRepaid;
    if (status === 'Repaid') return LoanStatus.Repaid;
    // Fallback to Active if string doesn't match
    return LoanStatus.Active;
  }
  if (typeof status === 'number') {
    return status as LoanStatus;
  }
  // If it's already LoanStatus enum, return as is
  return status;
};

// Компонент форми для додавання платежу
interface AddPaymentFormProps {
  loan: Loan;
  onSuccess: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

const AddPaymentForm: React.FC<AddPaymentFormProps> = ({
  loan,
  onSuccess,
  onCancel,
  isSubmitting,
  setIsSubmitting
}) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: toLocalDateString(new Date()),
    description: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Сума платежу повинна бути більшою за нуль';
    }

    const amount = parseFloat(formData.amount);
    const remainingAmount = loan.amount - loan.repaidAmount;

    if (amount > remainingAmount) {
      newErrors.amount = `Сума платежу не може перевищувати залишок позики (${formatCurrency(remainingAmount)})`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const request: CreateLoanPaymentRequest = {
        loanId: loan.id,
        amount: amount,
        paymentDate: formData.paymentDate ? toUTCDateString(formData.paymentDate) : undefined,
        description: formData.description || undefined
      };
      await loanPaymentService.create(request);
      showNotification('Платіж успішно додано', 'success');
      onSuccess();
      onCancel();
      setFormData({
        amount: '',
        paymentDate: toLocalDateString(new Date()),
        description: ''
      });
      setErrors({});
    } catch (error: any) {
      showNotification(error.message || 'Помилка збереження платежу', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="add-payment-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="payment-amount">Сума платежу</label>
        <input
          type="number"
          id="payment-amount"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          className={errors.amount ? 'form-input error' : 'form-input'}
          placeholder="0.00"
          required
          min="0.01"
          step="0.01"
        />
        {errors.amount && (
          <span className="error-message">{errors.amount}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="payment-date">Дата платежу (необов'язково)</label>
        <div className="date-input-wrapper">
          <input
            type="date"
            id="payment-date"
            value={formData.paymentDate}
            onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
            className="form-input date-input-custom"
          />
          <label htmlFor="payment-date" className="date-input-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </label>
        </div>
        <small style={{ color: 'var(--text-secondary)', fontSize: '0.85em', display: 'block', marginTop: '4px' }}>
          Якщо не вказано, буде використано поточну дату і час
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="payment-description">Опис (необов'язково)</label>
        <textarea
          id="payment-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="form-input"
          rows={3}
          placeholder="Додайте опис платежу"
        />
      </div>
    </form>
  );
};

const AccountsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Читаємо таб з URL або використовуємо 'accounts' за замовчуванням
  const tabFromUrl = searchParams.get('tab') as TabType;
  const isValidTab = tabFromUrl && ['accounts', 'transfers', 'loans'].includes(tabFromUrl);
  const [activeTab, setActiveTab] = useState<TabType>(isValidTab ? tabFromUrl : 'accounts');
  
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    initialBalance: '',
    currency: Currency.UAH as Currency,
    isSavings: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState<Record<string, boolean>>({});
  const [showConfirmDelete, setShowConfirmDelete] = useState<string>('');
  const [showConfirmCloseForm, setShowConfirmCloseForm] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedAccountForTransfer, setSelectedAccountForTransfer] = useState<Account | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [showConfirmDeleteTransfer, setShowConfirmDeleteTransfer] = useState<string>('');
  const [editingTransfer, setEditingTransfer] = useState<Transfer | null>(null);
  const [collapsedTransfers, setCollapsedTransfers] = useState<Set<string>>(new Set());
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null);
  const [showConfirmDeleteLoan, setShowConfirmDeleteLoan] = useState<string>('');
  const [collapsedLoans, setCollapsedLoans] = useState<Set<string>>(new Set());
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [selectedLoanForPayment, setSelectedLoanForPayment] = useState<Loan | null>(null);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [showConfirmDeletePayment, setShowConfirmDeletePayment] = useState<string>('');

  // Синхронізуємо activeTab з URL при зміні searchParams
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as TabType;
    if (tabFromUrl && ['accounts', 'transfers', 'loans'].includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    } else if (!tabFromUrl && activeTab !== 'accounts') {
      // Якщо немає табу в URL, встановлюємо 'accounts' за замовчуванням
      setSearchParams({ tab: 'accounts' }, { replace: true });
    }
  }, [searchParams, activeTab, setSearchParams]);

  // Перше завантаження рахунків
  useEffect(() => {
    loadAccounts(true);
  }, []);

  // Завантаження даних при перемиканні табів (без основного спінера)
  useEffect(() => {
    if (activeTab === 'transfers') {
      loadTransfers();
    } else if (activeTab === 'loans') {
      loadLoans();
    } else if (activeTab === 'accounts') {
      // Завантажуємо позики для відображення потенційних коштів на картках рахунків
      loadLoans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // За замовчуванням всі трансфери згорнуті
    if (transfers.length > 0) {
      setCollapsedTransfers(prev => {
        const newSet = new Set(prev);
        transfers.forEach(t => {
          if (!newSet.has(t.id)) {
            newSet.add(t.id);
          }
        });
        return newSet;
      });
    }
  }, [transfers]);

  const toggleTransferCollapse = (transferId: string) => {
    setCollapsedTransfers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transferId)) {
        newSet.delete(transferId);
      } else {
        newSet.add(transferId);
      }
      return newSet;
    });
  };

  const loadAccounts = async (showMainLoader = false) => {
    try {
      if (showMainLoader) {
        setLoading(true);
      }
      const data = await accountService.getAll();
      setAccounts(data);
    } catch (error) {
      showNotification('Помилка завантаження рахунків', 'error');
    } finally {
      if (showMainLoader) {
        setLoading(false);
      }
    }
  };

  const loadTransfers = async () => {
    try {
      setLoadingTransfers(true);
      const data = await transferService.getAll();
      setTransfers(data);
    } catch (error) {
      showNotification('Помилка завантаження трансферів', 'error');
    } finally {
      setLoadingTransfers(false);
    }
  };

  const loadLoans = async () => {
    try {
      setLoadingLoans(true);
      const data = await loanService.getAll();
      // Примусово оновлюємо стан, щоб гарантувати оновлення UI
      setLoans([...data]);
      // За замовчуванням всі позики згорнуті
      setCollapsedLoans(prev => {
        const newSet = new Set(prev);
        data.forEach(loan => {
          if (!newSet.has(loan.id)) {
            newSet.add(loan.id);
          }
        });
        return newSet;
      });
    } catch (error) {
      showNotification('Помилка завантаження позик', 'error');
    } finally {
      setLoadingLoans(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    await loadAccounts(false);
    if (activeTab === 'transfers') {
      await loadTransfers();
    } else {
      await loadLoans();
    }
  }, [activeTab]);

  useRefresh(handleRefresh);

  const handleShowForm = (account?: Account) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        name: account.name,
        initialBalance: account.initialBalance.toString(),
        currency: parseCurrency(account.currency),
        isSavings: !!account.isSavings
      });
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        initialBalance: '',
        currency: Currency.UAH,
        isSavings: false
      });
    }
    setFormErrors({});
    setShowForm(true);
  };

  const hasFormChanges = () => {
    if (editingAccount) {
      // Перевірка змін при редагуванні
      return formData.name.trim() !== editingAccount.name || formData.currency !== editingAccount.currency || formData.isSavings !== !!editingAccount.isSavings;
    } else {
      // Перевірка змін при створенні
      return formData.name.trim() !== '' || formData.initialBalance !== '' || formData.isSavings;
    }
  };

  const handleCloseFormAttempt = () => {
    if (hasFormChanges()) {
      setShowConfirmCloseForm(true);
    } else {
      handleCloseForm();
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAccount(null);
    setFormData({ name: '', initialBalance: '', currency: Currency.UAH, isSavings: false });
    setFormErrors({});
    setShowConfirmCloseForm(false);
  };

  const handleConfirmCloseForm = () => {
    handleCloseForm();
  };

  const handleCancelCloseForm = () => {
    setShowConfirmCloseForm(false);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Назва рахунку обов\'язкова';
    } else if (formData.name.trim().length < ValidationConstants.Account.NameMinLength) {
      errors.name = `Назва рахунку має бути не менше ${ValidationConstants.Account.NameMinLength} символів`;
    } else if (formData.name.trim().length > ValidationConstants.Account.NameMaxLength) {
      errors.name = `Назва рахунку не може перевищувати ${ValidationConstants.Account.NameMaxLength} символів`;
    } else if (ValidationUtils.containsForbiddenChars(formData.name)) {
      errors.name = 'Назва рахунку містить заборонені символи (<, >, ", &, \')';
    }

    if (!editingAccount) {
      const balance = parseFloat(formData.initialBalance);
      if (formData.initialBalance === '' || isNaN(balance)) {
        errors.initialBalance = 'Початковий баланс обов\'язковий';
      } else if (balance < ValidationConstants.Account.MinBalance) {
        errors.initialBalance = 'Початковий баланс не може бути від\'ємним';
      } else if (balance > ValidationConstants.Account.MaxBalance) {
        errors.initialBalance = `Початковий баланс не може перевищувати ${ValidationConstants.Account.MaxBalance.toLocaleString('uk-UA')}`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    try {
      setIsSubmitting(true);

      if (editingAccount) {
        const updateData: UpdateAccountRequest = {
          name: formData.name.trim(),
          currency: formData.currency,
          isDefault: editingAccount.isDefault,
          isSavings: formData.isSavings
        };
        await accountService.update(editingAccount.id, updateData);
      } else {
        const createData: CreateAccountRequest = {
          name: formData.name.trim(),
          initialBalance: parseFloat(formData.initialBalance),
          currency: formData.currency,
          isSavings: formData.isSavings
        };
        await accountService.create(createData);
      }

      await loadAccounts();
      handleCloseForm();
      showNotification(
        editingAccount ? 'Рахунок успішно оновлено' : 'Рахунок успішно створено',
        'success'
      );
    } catch (error: any) {
      setFormErrors({
        general: error.message || 'Помилка збереження рахунку'
      });
      showNotification('Помилка збереження рахунку', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (account: Account) => {
    setShowConfirmDelete(account.id);
  };

  const handleDeleteConfirm = async () => {
    const accountId = showConfirmDelete;
    if (!accountId || isDeletingAccount[accountId]) return;
    
    setIsDeletingAccount(prev => ({ ...prev, [accountId]: true }));
    
    try {
      await accountService.delete(accountId);
      await loadAccounts();
      showNotification('Рахунок успішно видалено', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення рахунку', 'error');
    } finally {
      setIsDeletingAccount(prev => ({ ...prev, [accountId]: false }));
      setShowConfirmDelete('');
    }
  };

  const handleDeleteCancel = () => {
    setShowConfirmDelete('');
  };

  const handleSetDefault = async (account: Account) => {
    if (account.isDefault) {
      return;
    }

    try {
      await accountService.update(account.id, {
        name: account.name,
        isDefault: true
      });
      await loadAccounts();
      showNotification('Дефолтний рахунок успішно встановлено', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка встановлення дефолтного рахунку', 'error');
    }
  };

  const handleTransferClick = (account: Account | null = null) => {
    setSelectedAccountForTransfer(account);
    setEditingTransfer(null);
    setShowTransferModal(true);
  };

  const handleEditTransferClick = (transfer: Transfer) => {
    const fromAccount = accounts.find(acc => acc.id === transfer.fromAccountId);
    if (fromAccount) {
      setSelectedAccountForTransfer(fromAccount);
      setEditingTransfer(transfer);
      setShowTransferModal(true);
    }
  };

  const handleTransferSuccess = async (updatedTransfer?: Transfer) => {
    if (editingTransfer && updatedTransfer) {
      setTransfers(prevTransfers => 
        prevTransfers.map(t => t.id === updatedTransfer.id ? updatedTransfer : t)
      );
      await loadAccounts();
      showNotification('Трансфер успішно оновлено', 'success');
    } else {
      await loadAccounts();
      await loadTransfers();
      showNotification('Трансфер успішно створено', 'success');
    }
  };

  const handleTransferClose = () => {
    setShowTransferModal(false);
    setSelectedAccountForTransfer(null);
    setEditingTransfer(null);
  };

  const handleDeleteTransferClick = (transferId: string) => {
    setShowConfirmDeleteTransfer(transferId);
  };

  const handleDeleteTransferConfirm = async () => {
    const transferId = showConfirmDeleteTransfer;
    if (!transferId) return;

    try {
      await transferService.delete(transferId);
      await loadAccounts();
      await loadTransfers();
      showNotification('Трансфер успішно видалено', 'success');
      setShowConfirmDeleteTransfer('');
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення трансферу', 'error');
    }
  };

  const handleDeleteTransferCancel = () => {
    setShowConfirmDeleteTransfer('');
  };

  const handleLoanClick = () => {
    setEditingLoan(null);
    setShowLoanModal(true);
  };

  const handleEditLoanClick = (loan: Loan) => {
    setEditingLoan(loan);
    setShowLoanModal(true);
  };

  const handleLoanSuccess = async (updatedLoan?: Loan) => {
    if (editingLoan && updatedLoan) {
      setLoans(prevLoans =>
        prevLoans.map(l => l.id === updatedLoan.id ? updatedLoan : l)
      );
      await loadAccounts();
      showNotification('Позику успішно оновлено', 'success');
    } else {
      await loadAccounts();
      await loadLoans();
      showNotification('Позику успішно створено', 'success');
    }
  };

  const toggleLoanCollapse = (loanId: string) => {
    setCollapsedLoans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  };

  const handleAddPaymentClick = (loan: Loan) => {
    setSelectedLoanForPayment(loan);
    setShowAddPaymentModal(true);
  };

  const handleAddPaymentModalClose = () => {
    setShowAddPaymentModal(false);
    setSelectedLoanForPayment(null);
  };

  const handlePaymentChange = async () => {
    // Невелика затримка, щоб дати бекенду час оновити дані після збереження платежу
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Перезавантажуємо весь список позик, щоб отримати актуальні дані з бекенду
    // Бекенд тепер повертає позики з включеними платежами
    await loadLoans();
    await loadAccounts();
  };

  const handleLoanClose = () => {
    setShowLoanModal(false);
    setEditingLoan(null);
  };

  const handleDeleteLoanClick = (loanId: string) => {
    setShowConfirmDeleteLoan(loanId);
  };

  const handleDeleteLoanConfirm = async () => {
    const loanId = showConfirmDeleteLoan;
    if (!loanId) return;

    try {
      await loanService.delete(loanId);
      await loadAccounts();
      await loadLoans();
      showNotification('Позику успішно видалено', 'success');
      setShowConfirmDeleteLoan('');
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення позики', 'error');
    }
  };

  const handleDeleteLoanCancel = () => {
    setShowConfirmDeleteLoan('');
  };

  const handleDeletePaymentClick = (paymentId: string) => {
    setShowConfirmDeletePayment(paymentId);
  };

  const handleDeletePaymentConfirm = async () => {
    const paymentId = showConfirmDeletePayment;
    if (!paymentId) return;

    try {
      await loanPaymentService.delete(paymentId);
      showNotification('Платіж успішно видалено', 'success');
      setShowConfirmDeletePayment('');
      await handlePaymentChange(); // Оновлюємо позики та рахунки
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення платежу', 'error');
    }
  };

  const handleDeletePaymentCancel = () => {
    setShowConfirmDeletePayment('');
  };

  if (loading) {
    return (
      <div className="accounts-page">
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження рахунків...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <div className="accounts-title">
          <h2>Управління рахунками</h2>
        </div>
      </div>

      {/* Таби */}
      <div className="card accounts-tabs-card">
        <div className="accounts-tab-selector">
          <button 
            className={`accounts-tab-btn ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => {
              setSearchParams({ tab: 'accounts' });
            }}
          >
            Рахунки
          </button>
          <button 
            className={`accounts-tab-btn ${activeTab === 'transfers' ? 'active' : ''}`}
            onClick={() => {
              setSearchParams({ tab: 'transfers' });
            }}
          >
            Трансфери
          </button>
          <button 
            className={`accounts-tab-btn ${activeTab === 'loans' ? 'active' : ''}`}
            onClick={() => {
              setSearchParams({ tab: 'loans' });
            }}
          >
            Позики
          </button>
        </div>
      </div>

      {/* Контент для табу "Рахунки" */}
      {activeTab === 'accounts' && (
        <>
          {/* Загальний капітал в USD */}
          {accounts.length > 0 && <TotalCapital accountsCount={accounts.length} />}

          <div className="accounts-list-header">
            <span className="accounts-count">Кількість рахунків: {accounts.length}</span>
            <button 
              className="add-account-btn"
              onClick={() => handleShowForm()}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12h20"></path>
              </svg>
              <span>Додати рахунок</span>
            </button>
          </div>

          <div className="accounts-list">
        {accounts.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-icon">🏦</div>
            <div className="empty-title">Немає рахунків</div>
            <div className="empty-text">
              Створіть свій перший рахунок для початку роботи з додатком
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => handleShowForm()}
            >
              Створити рахунок
            </button>
          </div>
        ) : (
          accounts.map((account) => {
            // Обчислюємо потенційні кошти (неповернені позики типу ILent для цього рахунку)
            const potentialFunds = loans
              .filter(loan => loan.accountId === account.id && isILent(loan.loanType))
              .reduce((sum, loan) => {
                // Використовуємо payments для точного обчислення поверненої суми
                const repaidAmount = loan.payments?.reduce((total, payment) => total + payment.amount, 0) || loan.repaidAmount || 0;
                const remaining = loan.amount - repaidAmount;
                return sum + Math.max(0, remaining);
              }, 0);

            return (
            <div key={account.id} className="card account-item">
              <div className="account-card-header">
                <div className="account-card-title-section">
                  <div className="account-title-content">
                    <h3 className="account-name">
                      {account.name}
                    </h3>
                    {account.isSavings && (
                      <span className="budget-type-badge account-savings-badge">Заощадження</span>
                    )}
                  </div>
                </div>
                <div className="account-actions">
                  <button
                    className="account-action-icon"
                    onClick={() => handleTransferClick(account)}
                    title="Трансфер коштів"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M7.83 13L9.41 11.41L8 10L4 14L8 18L9.41 16.59L7.83 15H20V13H7.83ZM16.17 11L14.59 12.59L16 14L20 10L16 6L14.59 7.41L16.17 9H4V11H16.17Z" fill="currentColor"/>
                    </svg>
                  </button>
                  {account.isDefault ? (
                    <span className="account-action-icon default-star-icon" title="За замовчуванням">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="#f59e0b" stroke="#f59e0b"></polygon>
                      </svg>
                    </span>
                  ) : (
                    <button
                      className="account-action-icon"
                      onClick={() => handleSetDefault(account)}
                      title="Встановити за замовчуванням"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                    </button>
                  )}
                  <button
                    className="account-action-icon"
                    onClick={() => handleShowForm(account)}
                    title="Редагувати"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    className="account-action-icon account-action-icon-danger"
                    onClick={() => handleDeleteClick(account)}
                    disabled={isDeletingAccount[account.id]}
                    title={isDeletingAccount[account.id] ? "Видалення..." : "Видалити"}
                  >
                    {isDeletingAccount[account.id] ? (
                      <div className="spinner-small"></div>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="account-card-body">
                <div style={{ display: 'flex', gap: 'var(--spacing-md)', width: '100%' }}>
                  <div className="account-balance-display" style={{ flex: 1 }}>
                    <div className="account-balance-label">Баланс</div>
                    <div className="account-balance-value">
                      {formatCurrency(account.currentBalance)} {getCurrencyName(parseCurrency(account.currency))}
                    </div>
                  </div>
                  {potentialFunds > 0 && (
                    <div className="account-balance-display" style={{ flex: 1 }}>
                      <div className="account-balance-label">Мені повернуть</div>
                      <div className="account-balance-value" style={{ color: '#f59e0b' }}>{formatCurrency(potentialFunds)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })
        )}
          </div>
        </>
      )}

      {/* Контент для табу "Трансфери" */}
      {activeTab === 'transfers' && (
        <>
          <div className="transfers-list-header">
            <span className="transfers-count">Кількість трансферів: {transfers.length}</span>
            <button 
              className="add-account-btn"
              onClick={() => {
                if (accounts.length > 0) {
                  handleTransferClick(null);
                } else {
                  showNotification('Спочатку створіть рахунок', 'warning');
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12h20"></path>
              </svg>
              <span>Додати трансфер</span>
            </button>
          </div>

          {loadingTransfers ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження трансферів...</p>
              </div>
            </div>
          ) : transfers.length === 0 ? (
            <div className="empty-state card">
              <div className="empty-icon">💸</div>
              <div className="empty-title">Немає трансферів</div>
              <div className="empty-text">
                Створіть свій перший трансфер між рахунками
              </div>
            </div>
          ) : (
            <div className="transfers-list">
              {transfers.map((transfer) => {
                const isCollapsed = collapsedTransfers.has(transfer.id);
                return (
                  <div key={transfer.id} className={`card transfer-item ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="account-card-header">
                      <div 
                        className="account-card-title-section transfer-title-section"
                        onClick={() => toggleTransferCollapse(transfer.id)}
                      >
                        <div className="transfer-expand-icon-wrapper">
                          <svg 
                            className={`transfer-expand-icon ${isCollapsed ? 'collapsed' : ''}`}
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                        <div className="account-title-content">
                          <h3 className="account-name">
                            <span className="transfer-from">{transfer.fromAccountName}</span>
                            <span className="transfer-arrow">→</span>
                            <span className="transfer-to">{transfer.toAccountName}</span>
                          </h3>
                          <p className="account-balance">
                            Сума: <span className="balance-amount">{formatCurrency(transfer.fromAmount)}</span>
                            {transfer.toAmount && transfer.fromAmount !== transfer.toAmount && (
                              <>
                                <span className="transfer-equals"> = </span>
                                <span className="balance-amount">{formatCurrency(transfer.toAmount)}</span>
                              </>
                            )}
                          </p>
                          {!isCollapsed && (
                            <>
                              <p className="transfer-type">
                                Тип: <span className="transfer-type-value">
                                  {transfer.transferType === TransferType.Savings ? 'Заощадження' : 'Звичайний переказ'}
                                </span>
                              </p>
                              <p className="transfer-date">
                                Дата: <span className="date-value">{new Date(transfer.date).toLocaleDateString('uk-UA', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</span>
                              </p>
                              {transfer.description && (
                                <p className="transfer-description">
                                  Коментар: <span className="description-value">{transfer.description}</span>
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="account-actions">
                        <button
                          className="account-action-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTransferClick(transfer);
                          }}
                          title="Редагувати трансфер"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className="account-action-icon account-action-icon-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTransferClick(transfer.id);
                          }}
                          title="Видалити трансфер"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Контент для табу "Позики" */}
      {activeTab === 'loans' && (
        <>
          <div className="loans-list-header">
            <span className="loans-count">Кількість позик: {loans.length}</span>
            <button 
              className="add-account-btn"
              onClick={handleLoanClick}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12h20"></path>
              </svg>
              <span>Додати позику</span>
            </button>
          </div>

          {loadingLoans ? (
            <div className="card">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження позик...</p>
              </div>
            </div>
          ) : loans.length === 0 ? (
            <div className="empty-state card">
              <div className="empty-title">Немає позик</div>
              <div className="empty-text">
                Створіть свою першу позику
              </div>
            </div>
          ) : (
            <div className="loans-list">
              {loans.map((loan) => {
                const repaymentProgress = loan.amount > 0 ? (loan.repaidAmount / loan.amount) * 100 : 0;
                const remaining = loan.amount - loan.repaidAmount;
                const parsedStatus = parseLoanStatus(loan.status);
                const statusClass = parsedStatus === LoanStatus.Repaid ? 'completed' : 
                                   parsedStatus === LoanStatus.PartiallyRepaid ? 'active' : 'active';
                const isCollapsed = collapsedLoans.has(loan.id);
                const payments = loan.payments || [];
                
                return (
                  <div key={loan.id} className={`budget-card loan-card loan-card-${statusClass} ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="budget-card-header">
                      <div 
                        className="budget-card-title-section"
                        onClick={() => toggleLoanCollapse(loan.id)}
                      >
                        <div className="budget-status-indicator-wrapper">
                          <div className={`budget-status-indicator budget-status-indicator-${statusClass}`}></div>
                          <svg 
                            className="budget-expand-icon"
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                        <div className="budget-title-content">
                          <h3 className="budget-card-title">
                            {isILent(loan.loanType) ? 'Я позичив' : 'Мені позичили'}: {loan.contactName}
                          </h3>
                          <div className="budget-card-meta">
                            <span className="budget-date-range">
                              {new Date(loan.date).toLocaleDateString('uk-UA', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                              {loan.dueDate && (
                                <> - {new Date(loan.dueDate).toLocaleDateString('uk-UA', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}</>
                              )}
                            </span>
                            {loan.accountName && (
                              <>
                                <span className="budget-type-badge">{loan.accountName}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="budget-card-actions">
                        <button
                          className="budget-action-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddPaymentClick(loan);
                          }}
                          title="Додати платіж"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v20M2 12h20"></path>
                          </svg>
                        </button>
                        <button
                          className="budget-action-icon"
                          onClick={() => handleEditLoanClick(loan)}
                          title="Редагувати позику"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className="budget-action-icon budget-action-icon-danger"
                          onClick={() => handleDeleteLoanClick(loan.id)}
                          title="Видалити позику"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="budget-card-body">
                      <div className="budget-stats-grid">
                        <div className="budget-stat">
                          <div className="budget-stat-label">Сума позики</div>
                          <div className="budget-stat-value budget-stat-total">{formatCurrency(loan.amount)}</div>
                        </div>
                        <div className="budget-stat">
                          <div className="budget-stat-label">Повернено</div>
                          <div className="budget-stat-value budget-stat-spent">{formatCurrency(loan.repaidAmount)}</div>
                        </div>
                        <div className={`budget-stat ${repaymentProgress >= 100 ? 'budget-stat-progress-good' : repaymentProgress >= 50 ? 'budget-stat-progress-warning' : 'budget-stat-progress-over'}`}>
                          <div className="budget-stat-label">Залишок</div>
                          <div className={`budget-stat-value budget-stat-remaining ${remaining <= 0 ? 'budget-stat-over' : ''}`}>
                            {formatCurrency(Math.max(0, remaining))}
                          </div>
                        </div>
                      </div>

                      <div className="budget-progress-wrapper">
                        <div className="budget-progress-header">
                          <span className="budget-progress-label">Прогрес погашення</span>
                          <span className="budget-progress-percentage">{repaymentProgress.toFixed(1)}%</span>
                        </div>
                        <div className="budget-progress-bar">
                          <div 
                            className={`budget-progress-fill budget-progress-${repaymentProgress >= 100 ? 'good' : repaymentProgress >= 50 ? 'warning' : 'over'}`}
                            style={{ width: `${Math.min(repaymentProgress, 100)}%` }}
                          ></div>
                          {repaymentProgress > 100 && (
                            <div 
                              className="budget-progress-overfill"
                              style={{ width: `${repaymentProgress - 100}%` }}
                            ></div>
                          )}
                        </div>
                      </div>

                      {loan.description && (
                        <div style={{ marginTop: 'var(--spacing-md)', paddingTop: 'var(--spacing-md)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9em', margin: 0 }}>
                            <strong>Коментар:</strong> {loan.description}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Колапсабельна секція з платежами */}
                    {!isCollapsed && (
                      <div className="budget-categories-section loan-payments-section">
                        <div className="budget-categories-header">
                          <span className="budget-categories-title">Платежі</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                            <span className="budget-categories-count">{payments.length}</span>
                            <button
                              className="budget-action-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddPaymentClick(loan);
                              }}
                              title="Додати платіж"
                              style={{ padding: '4px' }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2v20M2 12h20"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                        {payments.length === 0 ? (
                          <div style={{ padding: 'var(--spacing-md)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <p>Немає платежів</p>
                          </div>
                        ) : (
                          <div className="budget-categories-list">
                            {payments.map((payment) => (
                              <div key={payment.id} className="budget-category-card loan-payment-card">
                                <div className="budget-category-header">
                                  <span className="budget-category-name">
                                    {new Date(payment.paymentDate).toLocaleDateString('uk-UA', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                    {payment.description && ` - ${payment.description}`}
                                  </span>
                                  <div className="budget-category-amounts" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="budget-category-planned">{formatCurrency(payment.amount)}</span>
                                    <button
                                      className="budget-action-icon budget-action-icon-danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePaymentClick(payment.id);
                                      }}
                                      title="Видалити платіж"
                                      style={{ padding: '4px', width: '24px', height: '24px' }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3,6 5,6 21,6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}


      <Modal
        isOpen={showForm}
        onClose={handleCloseFormAttempt}
        title={editingAccount ? 'Редагувати рахунок' : 'Створити рахунок'}
        size="sm"
        footer={(
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseFormAttempt}
              disabled={isSubmitting}
            >
              Скасувати
            </button>
            <button
              form="account-form"
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (editingAccount ? 'Збереження...' : 'Створення...')
                : (editingAccount ? 'Зберегти' : 'Створити')
              }
            </button>
          </>
        )}
      >
        <form id="account-form" onSubmit={handleSubmit} className="account-form">
          <div className="form-group">
            <label htmlFor="account-name">Назва рахунку</label>
            <input
              type="text"
              id="account-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formErrors.name ? 'form-input error' : 'form-input'}
              placeholder="Наприклад: Основний банк"
              required
              minLength={ValidationConstants.Account.NameMinLength}
              maxLength={ValidationConstants.Account.NameMaxLength}
            />
            {formErrors.name && (
              <span className="error-message">{formErrors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="account-currency">Валюта</label>
            <GlassSelect
              options={[
                { value: String(Currency.UAH), label: 'UAH - Українська гривня' },
                { value: String(Currency.USD), label: 'USD - Долар США' },
                { value: String(Currency.EUR), label: 'EUR - Євро' },
                { value: String(Currency.GBP), label: 'GBP - Британський фунт' },
                { value: String(Currency.PLN), label: 'PLN - Польський злотий' },
                { value: String(Currency.JPY), label: 'JPY - Японська єна' },
                { value: String(Currency.CAD), label: 'CAD - Канадський долар' },
                { value: String(Currency.AUD), label: 'AUD - Австралійський долар' },
                { value: String(Currency.CHF), label: 'CHF - Швейцарський франк' },
                { value: String(Currency.CNY), label: 'CNY - Китайський юань' },
                { value: String(Currency.RUB), label: 'RUB - Російський рубль' },
                { value: String(Currency.CZK), label: 'CZK - Чеська крона' },
                { value: String(Currency.DKK), label: 'DKK - Данська крона' },
                { value: String(Currency.HUF), label: 'HUF - Угорський форинт' },
                { value: String(Currency.RON), label: 'RON - Румунський лей' },
                { value: String(Currency.SEK), label: 'SEK - Шведська крона' },
                { value: String(Currency.NOK), label: 'NOK - Норвезька крона' },
                { value: String(Currency.TRY), label: 'TRY - Турецька ліра' },
                { value: String(Currency.ILS), label: 'ILS - Ізраїльський шекель' },
                { value: String(Currency.AED), label: 'AED - Дірхам ОАЕ' },
                { value: String(Currency.SAR), label: 'SAR - Саудівський ріал' },
                { value: String(Currency.KZT), label: 'KZT - Казахстанський тенге' },
                { value: String(Currency.BYN), label: 'BYN - Білоруський рубль' }
              ]}
              value={String(formData.currency)}
              onChange={(value) => setFormData({ ...formData, currency: parseInt(value) as Currency })}
              placeholder="Оберіть валюту"
              searchable={true}
              filterOptions={true}
            />
          </div>

          {!editingAccount && (
            <div className="form-group">
              <label htmlFor="initial-balance">Початковий баланс</label>
              <input
                type="number"
                id="initial-balance"
                value={formData.initialBalance}
                onChange={(e) => setFormData({ ...formData, initialBalance: e.target.value })}
                className={formErrors.initialBalance ? 'form-input error' : 'form-input'}
                placeholder="0.00"
                required
                min={ValidationConstants.Account.MinBalance}
                max={ValidationConstants.Account.MaxBalance}
                step="0.01"
              />
              {formErrors.initialBalance && (
                <span className="error-message">{formErrors.initialBalance}</span>
              )}
            </div>
          )}

          <div className="form-group account-savings-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isSavings}
                onChange={(e) => setFormData({ ...formData, isSavings: e.target.checked })}
              />
              <span>Рахунок для заощаджень</span>
            </label>
            <span className="account-savings-hint">
              Перекази на цей рахунок враховуються як заощадження в плані місяця, а його витрати не зменшують бюджет тижня
            </span>
          </div>

          {formErrors.general && (
            <div className="error-message general-error">
              {formErrors.general}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={!!showConfirmDelete}
        title="Видалити рахунок?"
        message="Ви впевнені, що хочете видалити цей рахунок? Цю дію неможливо скасувати."
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={showConfirmCloseForm}
        title="Скасувати зміни?"
        message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
        confirmText="Так"
        cancelText="Ні"
        onConfirm={handleConfirmCloseForm}
        onCancel={handleCancelCloseForm}
        variant="warning"
      />

      <TransferModal
        isOpen={showTransferModal}
        fromAccount={selectedAccountForTransfer}
        accounts={accounts}
        transfer={editingTransfer}
        onClose={handleTransferClose}
        onSuccess={handleTransferSuccess}
      />

      <ConfirmationModal
        isOpen={!!showConfirmDeleteTransfer}
        title="Видалити трансфер?"
        message="Ви впевнені, що хочете видалити цей трансфер? Цю дію неможливо скасувати."
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleDeleteTransferConfirm}
        onCancel={handleDeleteTransferCancel}
        variant="danger"
      />

      <LoanModal
        isOpen={showLoanModal}
        accounts={accounts}
        loan={editingLoan}
        onClose={handleLoanClose}
        onSuccess={handleLoanSuccess}
      />

      {/* Модалка для додавання платежу */}
      <Modal
        isOpen={showAddPaymentModal}
        onClose={handleAddPaymentModalClose}
        title="Додати платіж"
        size="sm"
        footer={(
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddPaymentModalClose}
              disabled={isSubmittingPayment}
            >
              Скасувати
            </button>
            <button
              form="add-payment-form"
              type="submit"
              className="btn btn-primary"
              disabled={isSubmittingPayment}
            >
              {isSubmittingPayment ? 'Додавання...' : 'Додати'}
            </button>
          </>
        )}
      >
        {selectedLoanForPayment && (
          <AddPaymentForm
            loan={selectedLoanForPayment}
            onSuccess={handlePaymentChange}
            onCancel={handleAddPaymentModalClose}
            isSubmitting={isSubmittingPayment}
            setIsSubmitting={setIsSubmittingPayment}
          />
        )}
      </Modal>

      <ConfirmationModal
        isOpen={!!showConfirmDeleteLoan}
        title="Видалити позику?"
        message="Ви впевнені, що хочете видалити цю позику? Цю дію неможливо скасувати."
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleDeleteLoanConfirm}
        onCancel={handleDeleteLoanCancel}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={!!showConfirmDeletePayment}
        title="Видалити платіж?"
        message="Ви впевнені, що хочете видалити цей платіж? Цю дію неможливо скасувати."
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleDeletePaymentConfirm}
        onCancel={handleDeletePaymentCancel}
        variant="danger"
      />
    </div>
  );
};

export default AccountsPage;
